package com.exura.pages.planCreator.components.seriesViews.inputs

import androidx.compose.runtime.*
import com.exura.features.plandefinition.domain.model.series.PartOfDay
import com.exura.features.plandefinition.planLocalization
import com.exura.ui.*
import com.exura.ui.modifiers.insetHoverableItemStyle
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

@Composable
internal fun PartsOfDaySelector(
    selectedParts: List<PartOfDay>,
    onSelectionChange: (List<PartOfDay>) -> Unit,
    mode: PlanCreatorInputMode,
    enabled: Boolean = true,
    modifier: Modifier = Modifier
) {
    val regularParts = listOf(
        PartOfDay.MORNING,
        PartOfDay.NOON,
        PartOfDay.AFTERNOON,
        PartOfDay.EVENING,
        PartOfDay.NIGHT
    )

    val isInteractive = enabled && mode == PlanCreatorInputMode.EDIT
    val isTodaySelected = selectedParts.contains(PartOfDay.TODAY)

    Box(
        modifier = modifier
            .borderRadius(12.px)
            .border {
                width(1.px)
                style(LineStyle.Solid)
                color(ExuraColors.GRAY)
            }
            .fillMaxWidth()
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(0.px),
            modifier = Modifier.fillMaxWidth()
        ) {
            // Top half - regular parts of day (5 options)
            Row(
                horizontalArrangement = Arrangement.spacedBy(0.px),
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth(),
            ) {
                regularParts.forEachIndexed { index, part ->
                    val isSelected = selectedParts.contains(part) && !isTodaySelected

                    val mod = if(index == 0) {
                        Modifier.borderRadius(topLeft = 8.px)
                    } else if(index == regularParts.lastIndex) {
                        Modifier.borderRadius(topRight = 8.px)
                    } else {
                        Modifier
                    }

                    PartOfDayOption(
                        part = part,
                        isSelected = isSelected,
                        isInteractive = isInteractive,
                        modifier = Modifier.weight(1f).fillMaxWidth().then(mod),
                        onClick = {
                            if (isInteractive) {
                                // Deselect TODAY if selecting regular parts
                                val newSelection = if (isSelected) {
                                    selectedParts - part
                                } else {
                                    (selectedParts - PartOfDay.TODAY) + part
                                }
                                onSelectionChange(newSelection)
                            }
                        }
                    )
                }
            }

            // Bottom half - TODAY option (full width bar)
            TodayOption(
                isSelected = isTodaySelected,
                isInteractive = isInteractive,
                onClick = {
                    if (isInteractive) {
                        val newSelection = if (isTodaySelected) {
                            selectedParts - PartOfDay.TODAY
                        } else {
                            // Clear all regular parts and select TODAY
                            listOf(PartOfDay.TODAY)
                        }
                        onSelectionChange(newSelection)
                    }
                }
            )
        }
    }
}

@Composable
private fun PartOfDayOption(
    part: PartOfDay,
    isSelected: Boolean,
    isInteractive: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = Modifier
            .height(40.px)
            .insetHoverableItemStyle(
                isSelected = isSelected,
                shadeColor = ExuraColors.BLUE.copy(alpha = 0.1),
                shadeSize = 8.px,
                hoverColor = if (isInteractive) ExuraColors.BLUE.copy(alpha = 0.05) else ExuraColors.TRANSPARENT,
                selectedColor = ExuraColors.BLUE.copy(alpha = 0.15),
                cursor = if (isInteractive) Cursor.Pointer else Cursor.Default,
                onClick = if (isInteractive) onClick else null,
                isHoverable = isInteractive
            )
            .then(modifier),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            text = planLocalization.current.partOfDayToString(part),
            modifier = Modifier
                .classNames(TextStyles.normal14)
                .color(ExuraColors.ARSENIC)
        )
    }
}

@Composable
private fun TodayOption(
    isSelected: Boolean,
    isInteractive: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(48.px)
            .insetHoverableItemStyle(
                isSelected = isSelected,
                shadeColor = ExuraColors.BLUE.copy(alpha = 0.1),
                shadeSize = 8.px,
                hoverColor = if (isInteractive) ExuraColors.BLUE.copy(alpha = 0.05) else ExuraColors.TRANSPARENT,
                selectedColor = ExuraColors.BLUE.copy(alpha = 0.15),
                cursor = if (isInteractive) Cursor.Pointer else Cursor.Default,
                onClick = if (isInteractive) onClick else null,
                isHoverable = isInteractive
            )
            .borderRadius(bottomLeft = 8.px, bottomRight = 8.px)
        ,
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            text = planLocalization.current.partOfDayToString(PartOfDay.TODAY),
            modifier = Modifier
                .classNames(TextStyles.medium16)
                .color(ExuraColors.ARSENIC)
        )
    }
}
